import * as React from 'react'

import Button from '@doordash/component-button'
import { Select, TextField } from '@doordash/component-fields'
import {
  Text,
  Breakpoints,
  InlineChildren,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import MediaQuery from 'react-responsive'
import Form from '../../../../utilities/Form'
// tslint:disable-next-line:max-line-length
import { validators as sharedValidators } from '../../../../utilities/validators'
import {
  checkNeedsLicenseInfo,
  processVehicleTypes,
} from '../../../../utilities/vehicle'
import { InsuranceCheckBox } from '../../../atoms/insurance-checkbox'
import VehicleTypeDropdown from '../../../atoms/vehicle-type-dropdown'

import { STATE_CODES } from '../../../../constants'

import t from '../strings'

import { BackgroundCheck as Tag } from '../../../../models/selectorAnchors'
import * as DA from '../../../../typeModules/events'

type Props = {
  uniqueLink?: string
  onSubmit?: any
  vehicleTypes?: any
  trackBackgroundCheckSteps: (data: DA.TrackBackgroundCheck) => Promise<any>
}

const licenseValidator = {
  vehicleType: sharedValidators.vehicleType,
  licenseNumber: sharedValidators.licenseNumber,
  licenseState: sharedValidators.licenseState,
  insuranceCheck: sharedValidators.insuranceCheck,
}

const noLicenseValidator = {
  vehicleType: sharedValidators.vehicleType,
  licenseNumber: sharedValidators.hidden,
  licenseState: sharedValidators.hidden,
  insuranceCheck: sharedValidators.hidden,
}

class VehicleStep extends React.Component<Props> {
  public state = {
    licenseRequired: false,
  }

  public handleVehicleChange = (name, handleChange) => selectedID => {
    handleChange(name, selectedID)

    let licenseRequired = this.state.licenseRequired
    if (this.props.vehicleTypes[selectedID].needs_license_info) {
      licenseRequired = true
    } else {
      licenseRequired = false
    }
    this.setState({ licenseRequired })
  }

  public componentDidMount() {
    this.props.trackBackgroundCheckSteps({
      page: DA.BackgroundCheckPages.Vehicle,
      action: DA.EventActions.Rendered,
    })
  }
  public render() {
    const { vehicleTypes, trackBackgroundCheckSteps } = this.props

    const defaultFields = {
      vehicleType: -1,
      licenseNumber: '',
      licenseState: '',
      insuranceCheck: '',
    }
    return (
      <React.Fragment>
        <Stack size={Stack.Sizes.XxSmall}>
          <Text styles={Text.Styles.PageTitle}>{t.vehicleDetailTitle()}</Text>
        </Stack>
        <StackChildren>
          <Text styles={Text.Styles.PageDescriptionBody}>
            {t.vehicleExplanation()}
          </Text>
          <Form
            onSubmit={this.props.onSubmit}
            validators={
              this.state.licenseRequired ? licenseValidator : noLicenseValidator
            }
            fields={defaultFields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <StackChildren size={StackChildren.Sizes.Large}>
                    <div data-anchor-id={Tag.VehicleSelect}>
                      <VehicleTypeDropdown
                        accessibilityDescribedBy={'vehicleType'}
                        label={t.vehicleTypeLabel()}
                        description={t.required()}
                        vehicleTypes={processVehicleTypes(vehicleTypes)}
                        onChange={this.handleVehicleChange(
                          'vehicleType',
                          handleChange
                        )}
                        {...fields.vehicleType}
                      />
                    </div>
                    {checkNeedsLicenseInfo(fields, vehicleTypes) && (
                      <React.Fragment>
                        <MediaQuery minWidth={Breakpoints.Desktop + 1}>
                          <InlineChildren>
                            <div
                              data-anchor-id={Tag.LicenseState}
                              style={{ width: '100%' }}
                            >
                              <Select
                                accessibilityDescribedBy={'licenseState'}
                                label={t.licenseStateLabel()}
                                description={t.required()}
                                disabledValue={t.licenseStateDisabled()}
                                onChange={handleChange.bind(
                                  this,
                                  'licenseState'
                                )}
                                options={STATE_CODES}
                                {...fields.licenseState}
                              />
                            </div>
                            <TextField
                              accessibilityDescribedBy={'licenseNumber'}
                              label={t.licenseNumberLabel()}
                              description={t.required()}
                              onChange={handleChange.bind(
                                this,
                                'licenseNumber'
                              )}
                              autoComplete="off"
                              data-anchor-id={Tag.LicenseNumber}
                              {...fields.licenseNumber}
                            />
                          </InlineChildren>
                        </MediaQuery>
                        <MediaQuery maxWidth={Breakpoints.Desktop}>
                          <div data-anchor-id={Tag.LicenseState}>
                            <Select
                              accessibilityDescribedBy={'licenseState'}
                              label={t.licenseStateLabel()}
                              description={t.required()}
                              disabledValue={t.licenseStateDisabled()}
                              onChange={handleChange.bind(this, 'licenseState')}
                              options={STATE_CODES}
                              data-anchor-id={Tag.LicenseState}
                              {...fields.licenseState}
                            />
                          </div>
                          <TextField
                            accessibilityDescribedBy={'licenseNumber'}
                            label={t.licenseNumberLabel()}
                            description={t.required()}
                            onChange={handleChange.bind(this, 'licenseNumber')}
                            autoComplete="off"
                            data-anchor-id={Tag.LicenseNumber}
                            {...fields.licenseNumber}
                          />
                        </MediaQuery>
                        <InsuranceCheckBox
                          id="inset-insurance"
                          accessibilityDescribedBy={'insuranceCheckbox'}
                          label={t.insuranceCertificationLabel()}
                          onChange={handleChange.bind(this, 'insuranceCheck')}
                          anchorId={Tag.InsuranceCheckbox}
                          {...fields.insuranceCheck}
                        />
                      </React.Fragment>
                    )}
                    <Button
                      accessibilityDescribedBy={'submitBackgroundCheck'}
                      onClick={async () => {
                        trackBackgroundCheckSteps({
                          page: DA.BackgroundCheckPages.Vehicle,
                          action: DA.EventActions.Continue,
                        })
                        try {
                          await handleSubmit()
                        } catch (e) {
                          this.props.trackBackgroundCheckSteps({
                            page: DA.BackgroundCheckPages.Vehicle,
                            action: DA.EventActions.Error,
                            value: e,
                          })
                        }
                      }}
                      state={
                        isLoading
                          ? Button.States.Loading
                          : Button.States.Default
                      }
                      data-anchor-id={Tag.PrimaryAction}
                    >
                      {t.continue()}
                    </Button>
                  </StackChildren>
                </form>
              )
            }}
          </Form>
        </StackChildren>
      </React.Fragment>
    )
  }
}

export default VehicleStep
